import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { teamMembers } from '../data/mock';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

export const TeamPage = () => {
  const [selectedDepartment, setSelectedDepartment] = useState('all');

  // Get unique departments
  const departments = ['all', ...new Set(teamMembers.map(member => member.department))];

  // Filter team members by department
  const filteredMembers = selectedDepartment === 'all' 
    ? teamMembers 
    : teamMembers.filter(member => member.department === selectedDepartment);

  // Group by department for display
  const membersByDepartment = filteredMembers.reduce((acc, member) => {
    if (!acc[member.department]) acc[member.department] = [];
    acc[member.department].push(member);
    return acc;
  }, {});

  const departmentOrder = ['Direction', 'Service Technique', 'Service Marketing', 'Service Administratif'];
  const sortedDepartments = departmentOrder.filter(dept => membersByDepartment[dept]);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-32 bg-gradient-to-br from-slate-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/"
            className="inline-flex items-center text-slate-600 hover:text-emerald-600 mb-8 transition-colors duration-200 group"
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform duration-200" />
            Retour à l'accueil
          </Link>

          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center space-x-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Users className="w-4 h-4" />
              <span>{teamMembers.length} Experts</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
              Notre Équipe
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Des professionnels passionnés et qualifiés, unis pour transformer vos projets digitaux en succès
            </p>
          </div>

          {/* Department Filter */}
          <div className="max-w-xs mx-auto">
            <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
              <SelectTrigger className="rounded-lg border-slate-300">
                <SelectValue placeholder="Filtrer par département" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les départements</SelectItem>
                <SelectItem value="Direction">Direction</SelectItem>
                <SelectItem value="Service Technique">Service Technique</SelectItem>
                <SelectItem value="Service Marketing">Service Marketing</SelectItem>
                <SelectItem value="Service Administratif">Service Administratif</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Team Members by Department */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {sortedDepartments.map((department, deptIndex) => (
            <div key={department} className={deptIndex > 0 ? 'mt-20' : ''}>
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-slate-900 mb-2">
                  {department === 'Direction' && '👨‍💼 '}
                  {department === 'Service Technique' && '💻 '}
                  {department === 'Service Marketing' && '📢 '}
                  {department === 'Service Administratif' && '📊 '}
                  {department}
                </h2>
                <div className="w-20 h-1 bg-emerald-600 rounded-full"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {membersByDepartment[department].map((member) => (
                  <Card 
                    key={member.id}
                    className="p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-2 border-slate-100 hover:border-emerald-200 rounded-2xl group"
                  >
                    <div className="text-center space-y-4">
                      {member.photo ? (
                        <div className="w-24 h-24 mx-auto rounded-full overflow-hidden ring-4 ring-emerald-100 group-hover:ring-emerald-300 transition-all duration-300">
                          <img 
                            src={member.photo} 
                            alt={member.name}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                        </div>
                      ) : (
                        <Avatar className="w-24 h-24 mx-auto bg-gradient-to-br from-emerald-600 to-emerald-500 group-hover:scale-110 transition-transform duration-300">
                          <AvatarFallback className="text-white font-semibold text-lg">
                            {member.avatar}
                          </AvatarFallback>
                        </Avatar>
                      )}
                      
                      <div>
                        <h3 className="text-lg font-bold text-slate-900 group-hover:text-emerald-600 transition-colors duration-200">
                          {member.name}
                        </h3>
                        <p className="text-sm font-medium text-emerald-600 mt-1">
                          {member.role}
                        </p>
                      </div>

                      <p className="text-sm text-slate-600 leading-relaxed">
                        {member.bio}
                      </p>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-emerald-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold mb-2">{teamMembers.length}</div>
              <div className="text-emerald-100">Experts</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">4</div>
              <div className="text-emerald-100">Départements</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">150+</div>
              <div className="text-emerald-100">Projets Réussis</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">98%</div>
              <div className="text-emerald-100">Satisfaction Client</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            Rejoignez Notre Équipe !
          </h2>
          <p className="text-xl text-slate-300 mb-8 leading-relaxed">
            Nous recherchons constamment des talents passionnés pour renforcer notre équipe. 
            Envoyez-nous votre candidature !
          </p>
          <Link to="/contact">
            <button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-10 py-4 text-lg font-semibold transition-all duration-200 transform hover:scale-105 shadow-lg">
              Postuler Maintenant
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};
